/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button, capitalize, Checkbox, Grid, Typography } from '@mui/material';

import { CriteriaOption } from 'commons/components/SearchRelated';
import { useUpdateQueryParams } from 'commons/components/SearchRelated/SearchWithCriteria/hooks';
import * as S from 'commons/components/Table/Table.styles';
import { InboundTaskDocumentStatusEnum } from 'commons/enums';
import { IInboundTaskDocument } from 'commons/types';
import { RowTitleProps, SortOrder } from 'commons/types/listPageData.interface';
import {
  DataTable,
  ListFilterCard,
  TableActionRow,
} from 'pages/dashboard/commons/components';
import { RootReducerInterface } from 'redux-stores/reducers';

import { InboundTaskItemDialogPage } from './InboundTaskItemDialogPage.component';

const rowTitles: RowTitleProps[] = [
  { title: 'Inbound Task Number' },
  { title: 'Status' },
  { title: 'Vendor' },
  { title: 'Total SKU(s)' },
  { title: 'Total QTY' },
];

const Row: React.FC<{
  data: IInboundTaskDocument;
  index: number;
  onCheckboxClick: (inboundTaskNumber: string) => void;
  checked: boolean;
  handleSetInboundTaskItemDetail: (value: IInboundTaskDocument) => void;
}> = ({
  data,
  index,
  onCheckboxClick,
  checked,
  handleSetInboundTaskItemDetail,
}) => {
  return (
    <S.TableRow>
      <S.TableColumn style={{ width: '5%' }}>
        <Checkbox
          data-testid={`InboundTaskDocumentListCheckBox{${index + 1}}`}
          onClick={() => onCheckboxClick(data.inbound_task_number)}
          checked={checked}
        />
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '20%' }}
        data-testid={`InboundTaskListDocumentNumber${index + 1}`}
      >
        {data.inbound_task_number}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '10%', textTransform: 'capitalize' }}
        data-testid={`InboundTaskDocumentListStatus-${index + 1}`}
      >
        {data.status.replace(/-/g, ' ')}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '20%', textTransform: 'capitalize' }}
        data-testid={`InboundTaskDocumentListVendor-${index + 1}`}
      >
        {data.vendor}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '10%' }}
        data-testid={`InboundTaskDocumentListTotalSKU-${index + 1}`}
      >
        {data.total_sku || '-'}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '10%' }}
        data-testid={`InboundTaskDocumentListTotalQTY-${index + 1}`}
      >
        {data.total_qty || '-'}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '10%', textAlign: 'right' }}
        data-testid={`InboundTaskDocumentListRowActionBtn-${index + 1}`}
      >
        <Button
          data-testid={`buttonInboundTaskDocumentListDetail-{${index + 1}}`}
          onClick={() => handleSetInboundTaskItemDetail(data)}
          variant="outlined"
          sx={{
            width: 87,
          }}
        >
          Details
        </Button>
      </S.TableColumn>
    </S.TableRow>
  );
};

export const InboundTaskDocumentListTable: React.FC = (): React.ReactNode => {
  /* CONSTANTS */
  const { queryParams, updateQueryParams } = useUpdateQueryParams();
  const { inboundTaskDocumentList } = useSelector(
    (state: RootReducerInterface) => state.dashboard,
  );

  const statusArrayText: {
    key: InboundTaskDocumentStatusEnum | 'all';
    text: string;
  }[] = [
    {
      key: 'all',
      text: 'All',
    },
    ...Object.values(InboundTaskDocumentStatusEnum).map((value) => ({
      key: value,
      text: capitalize(value.replace('-', ' ')),
    })),
  ];

  const searchCriteria: CriteriaOption[] = [
    {
      name: 'Inbound Task Number',
      id: 'inbound_task_numbers',
      isArray: true,
      parameterMapping: 'inbound_task_numbers',
    },
    {
      name: 'Vendor',
      id: 'vendors',
      isArray: true,
      allowSpace: true,
      parameterMapping: 'vendors',
    },
  ];

  /* HOOKS */
  const [checkAll, setCheckAll] = useState<boolean>(false);
  const [checkedDocuments, setCheckedDocuments] = useState<string[]>([]);
  const [showDetailInboundTaskDocument, setShowDetailInboundTaskDocument] =
    useState<IInboundTaskDocument | null>(null);

  const [statusFilter, setStatusFilter] =
    useState<InboundTaskDocumentStatusEnum | null>(null);

  useEffect(() => {
    updateQueryParams(
      { sort_by: 'createdAt', sort_order: SortOrder.DESC },
      { clearOther: true },
    );
  }, []);

  /* FUNCTIONS */
  const handleStatusFilter = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    value: InboundTaskDocumentStatusEnum,
  ): void => {
    setStatusFilter(value);
    updateQueryParams(
      { status: value },
      { clearQueryParams: ['page', 'integration_status'] },
    );
  };

  const selectedDocs = inboundTaskDocumentList.filter((document) =>
    checkedDocuments.includes(document.inbound_task_number),
  );

  const handleCheckDocument = (inboundTaskNumber: string): void => {
    if (checkedDocuments.includes(inboundTaskNumber)) {
      // remove inboundTaskNumber from checkedDocuments
      checkedDocuments.splice(checkedDocuments.indexOf(inboundTaskNumber), 1);
      setCheckedDocuments([...checkedDocuments]);
    } else {
      // insert inboundTaskNumber number to checkedDocuments
      setCheckedDocuments([...checkedDocuments, inboundTaskNumber]);
    }
  };

  const handleCheckAll = (): void => {
    if (checkAll) {
      // remove all check list
      setCheckedDocuments([]);
    } else {
      // add all inbound task number to checkedDocuments
      setCheckedDocuments(
        inboundTaskDocumentList.map((doc) => doc.inbound_task_number),
      );
    }
    setCheckAll(!checkAll);
  };

  const handleSetInboundTaskItemDetail = (
    value: IInboundTaskDocument | null,
  ): void => {
    setShowDetailInboundTaskDocument(value);
  };

  return (
    <>
      <ListFilterCard
        statusArrayText={statusArrayText}
        searchCriteria={searchCriteria}
        statusFilter={statusFilter}
        handleStatusFilter={handleStatusFilter}
      />
      <div style={{ marginTop: '24px' }}>
        {Object.keys(queryParams).includes('status') ? (
          <>
            <TableActionRow
              dataName="inbound task doc"
              selectedData={selectedDocs}
            />
            <DataTable
              rowTitles={rowTitles}
              checkAll={checkAll}
              handleCheckAll={handleCheckAll}
            >
              <S.TableBody>
                {inboundTaskDocumentList.map((item, index) => (
                  <Row
                    data={item}
                    index={index}
                    onCheckboxClick={handleCheckDocument}
                    checked={checkedDocuments.includes(
                      item.inbound_task_number,
                    )}
                    handleSetInboundTaskItemDetail={
                      handleSetInboundTaskItemDetail
                    }
                  />
                ))}
              </S.TableBody>
            </DataTable>
            {inboundTaskDocumentList.length > 0 && (
              <TableActionRow
                dataName="inbound task doc"
                selectedData={selectedDocs}
              />
            )}
            <InboundTaskItemDialogPage
              inboundTaskDocument={showDetailInboundTaskDocument}
              onClose={() => handleSetInboundTaskItemDetail(null)}
            />
          </>
        ) : (
          <Grid container justifyContent="center">
            <Typography>Please select a status</Typography>
          </Grid>
        )}
      </div>
    </>
  );
};
